// ExperimentsPane.tsx — the left pane, and the page's primary selection.
//
// Experiments are the top-level object: picking one here scopes the gitgraph
// to its branches and the overview pane to its values (requirement 4). The
// pane renders the lineage as an indented tree from `parent_id`, with each
// node's status, its position-ordered objectives, and its commit/metric/feed
// counts.
//
// Selection is written to the URL by the parent (`?exp=<slug>`), so this
// component holds no selection state of its own — only the dialog it has
// open and the error of the last action.
//
// Lineage actions map one-to-one onto the API: fork (child on a new branch),
// emancipate (become a root), consolidate (merge the branch into the
// parent's through a gitea pull request, then fold into the parent), edit
// (title/hypothesis/objectives/status), delete (admin only). The three that
// cannot be undone by clicking again go through ui.useConfirm().
import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import { BsPencil, BsPlusLg, BsTrash } from 'react-icons/bs';
import { GoGitMerge } from 'react-icons/go';
import { StatusBadge, unwrapJson, ui } from 'common';
import { apiClient } from '../api';
import type { Experiment, ExperimentStatus } from '../api';
import { isClosed, visibleExperiments } from './closed';
import ExperimentForm, { type ExperimentFormMode } from './ExperimentForm';

interface Props {
  owner: string;
  name: string;
  experiments: Experiment[];
  /** Slug of the selected experiment; '' when none resolved. */
  selected: string;
  onSelect: (slug: string) => void;
  descendants: boolean;
  onDescendantsChange: (next: boolean) => void;
  /** List consolidated and abandoned experiments too (closed.ts). */
  showClosed: boolean;
  onShowClosedChange: (next: boolean) => void;
  canWrite: boolean;
  isAdmin: boolean;
  loading: boolean;
  /** Set when the experiments route failed — the pane says so and stays usable. */
  loadError: string;
  /** Reload the list; `select` names an experiment to select afterwards. */
  onChanged: (select?: string) => void;
}

const STATUS_TONE: Record<ExperimentStatus, 'success' | 'info' | 'secondary'> = {
  active: 'success',
  consolidated: 'info',
  abandoned: 'secondary',
};

interface TreeNode {
  exp: Experiment;
  depth: number;
}

// Flatten the lineage depth-first. The server already returns roots-first,
// descendants-after order, but the tree is rebuilt from parent_id so the
// pane renders correctly whatever order it is handed.
export function lineage(experiments: Experiment[]): TreeNode[] {
  const byId = new Map(experiments.map((e) => [e.id, e]));
  const children = new Map<number, Experiment[]>();
  const roots: Experiment[] = [];
  for (const e of experiments) {
    const parent = e.parent_id && byId.has(e.parent_id) ? e.parent_id : 0;
    if (parent) {
      const arr = children.get(parent) ?? [];
      arr.push(e);
      children.set(parent, arr);
    } else {
      roots.push(e);
    }
  }
  const out: TreeNode[] = [];
  const seen = new Set<number>();
  const walk = (e: Experiment, depth: number) => {
    if (seen.has(e.id)) return; // defensive: a cyclic parent_id can't hang the UI
    seen.add(e.id);
    out.push({ exp: e, depth });
    for (const c of children.get(e.id) ?? []) walk(c, depth + 1);
  };
  for (const r of roots) walk(r, 0);
  // Anything unreachable (parent outside the list, cycle) still gets a row.
  for (const e of experiments) if (!seen.has(e.id)) out.push({ exp: e, depth: 0 });
  return out;
}

// The git half of a consolidation, as POST …/consolidate reports it.
interface MergeOutcome {
  status: 'merged' | 'up_to_date' | 'skipped';
  pr?: number;
  url?: string;
  head?: string;
  base?: string;
  reason?: string;
}

function primaryBranchOf(e: Experiment): string {
  return e.branches?.find((b) => b.role === 'primary')?.branch ?? '';
}

export default function ExperimentsPane({
  owner, name, experiments, selected, onSelect, descendants, onDescendantsChange,
  showClosed, onShowClosedChange,
  canWrite, isAdmin, loading, loadError, onChanged,
}: Props) {
  const [dialog, setDialog] = useState<ExperimentFormMode | null>(null);
  const [error, setError] = useState('');
  // What the last consolidation did in git, shown until the next action.
  const [mergeNote, setMergeNote] = useState<MergeOutcome | null>(null);
  const [confirm, confirmDialog] = ui.useConfirm();

  const current = experiments.find((e) => e.slug === selected) ?? null;
  // Closed experiments drop out of the tree unless asked for; a hidden
  // parent's open children become roots (lineage() handles that).
  const nodes = lineage(visibleExperiments(experiments, showClosed, selected));
  const closedCount = experiments.filter(isClosed).length;

  // POST /experiments/{slug}/<action>; the mutated experiment comes back and
  // the parent reloads the list around it.
  const act = async (slug: string, action: string, select?: string) => {
    setError('');
    setMergeNote(null);
    try {
      const res = await apiClient.fetch(
        `/api/v1/repos/${owner}/${name}/experiments/${encodeURIComponent(slug)}/${action}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
      );
      await unwrapJson<Experiment>(res);
      onChanged(select);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} experiment`);
    }
  };

  const emancipate = async (e: Experiment) => {
    if (!(await confirm({
      title: 'Emancipate Experiment',
      body: <>Make <code>{e.slug}</code> a root? It keeps its branches and
        results; only its lineage changes.</>,
      confirmLabel: 'Emancipate',
      variant: 'warning',
    }))) return;
    await act(e.slug, 'emancipate', e.slug);
  };

  // Consolidating merges the child's primary branch into the parent's
  // through a gitea pull request first; the server only folds the
  // experiment once that merge lands, and answers 409 (shown as the pane
  // error) when it cannot.
  const consolidate = async (e: Experiment) => {
    const parent = experiments.find((p) => p.id === e.parent_id);
    const head = primaryBranchOf(e);
    const base = parent ? primaryBranchOf(parent) : '';
    if (!(await confirm({
      title: 'Consolidate Experiment',
      body: <>Fold <code>{e.slug}</code> into{' '}
        <code>{parent ? parent.slug : 'its parent'}</code>?{' '}
        {head && base
          ? <>A pull request merges <code>{head}</code> into <code>{base}</code> on
            gitea, then </>
          : <>Then </>}
        its branches, metric values and feeds move to the parent and this
        experiment is closed.</>,
      confirmLabel: 'Merge & consolidate',
      variant: 'warning',
    }))) return;
    setError('');
    setMergeNote(null);
    try {
      const res = await apiClient.fetch(
        `/api/v1/repos/${owner}/${name}/experiments/${encodeURIComponent(e.slug)}/consolidate`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
      );
      const body = await unwrapJson<Experiment & { merge?: MergeOutcome }>(res);
      setMergeNote(body.merge ?? null);
      onChanged(parent?.slug);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to consolidate experiment');
    }
  };

  const remove = async (e: Experiment) => {
    if (!(await confirm({
      title: 'Delete Experiment',
      body: <>Delete <code>{e.slug}</code>? Every metric value and feed
        recorded under it goes with it.</>,
      confirmLabel: 'Delete',
    }))) return;
    setError('');
    try {
      const res = await apiClient.fetch(
        `/api/v1/repos/${owner}/${name}/experiments/${encodeURIComponent(e.slug)}`,
        { method: 'DELETE' },
      );
      if (!res.ok && res.status !== 204) await unwrapJson<unknown>(res);
      onChanged('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete experiment');
    }
  };

  return (
    <div className="card experiments-pane">
      <div className="card-header d-flex align-items-center gap-2">
        <strong>Experiments</strong>
        <span className="text-secondary ms-auto">{nodes.length}</span>
      </div>
      <div className="card-body experiments-tree">
        {error && <div className="alert alert-danger">{error}</div>}
        {mergeNote && (
          <div className={`alert ${mergeNote.status === 'skipped' ? 'alert-warning' : 'alert-success'}`}>
            {mergeNote.status === 'merged' && (
              <>Merged <code>{mergeNote.head}</code> into <code>{mergeNote.base}</code>
                {mergeNote.url
                  ? <> in <a href={mergeNote.url} target="_blank" rel="noreferrer">PR #{mergeNote.pr}</a></>
                  : null}.</>
            )}
            {mergeNote.status === 'up_to_date' && (
              <><code>{mergeNote.base}</code> already had everything on{' '}
                <code>{mergeNote.head}</code>; nothing to merge.</>
            )}
            {mergeNote.status === 'skipped' && <>Consolidated without a git merge: {mergeNote.reason}.</>}
          </div>
        )}
        {loadError && (
          <div className="alert alert-warning">
            <span className="alert-heading d-block">No experiments</span>
            {loadError}
          </div>
        )}
        {loading ? (
          <div className="text-secondary">Loading…</div>
        ) : nodes.length === 0 && experiments.length > 0 ? (
          <div className="text-secondary">
            Every experiment is consolidated or abandoned; the switch below shows them.
          </div>
        ) : nodes.length === 0 ? (
          <div className="text-secondary">
            Nothing tracked yet. Pushing a branch or posting a metric from one
            starts an experiment on its own; the button below starts one by hand.
          </div>
        ) : (
          <ul className="experiments-list">
            {nodes.map(({ exp, depth }) => (
              <li key={exp.id} style={{ paddingLeft: `${depth * 0.85}rem` }}>
                <button
                  type="button"
                  className={`btn btn-sm w-100 text-start experiment-row${
                    exp.slug === selected ? ' experiment-row-on' : ''
                  }`}
                  onClick={() => onSelect(exp.slug)}
                  aria-pressed={exp.slug === selected}
                >
                  <span className="d-flex align-items-center gap-2">
                    <span className="text-truncate">{exp.title || exp.slug}</span>
                    <StatusBadge tone={STATUS_TONE[exp.status] ?? 'neutral'} className="ms-auto">
                      {exp.status}
                    </StatusBadge>
                  </span>
                  <span className="d-block text-secondary experiment-meta">
                    <code>{exp.slug}</code>
                    {' · '}
                    {exp.commit_count} commit{exp.commit_count === 1 ? '' : 's'}
                    {' · '}
                    {exp.metric_count} metric{exp.metric_count === 1 ? '' : 's'}
                    {' · '}
                    {exp.feed_count} feed{exp.feed_count === 1 ? '' : 's'}
                  </span>
                  {(exp.objectives ?? []).length > 0 && (
                    <span className="d-flex flex-wrap gap-1 mt-1">
                      {(exp.objectives ?? []).map((o, i) => (
                        <StatusBadge
                          key={o.metric_id || o.name}
                          tone={i === 0 ? 'primary' : 'neutral'}
                          uppercase={false}
                        >
                          {i === 0 ? `★ ${o.name}` : o.name}
                        </StatusBadge>
                      ))}
                    </span>
                  )}
                  {(exp.branches ?? []).length > 0 && (
                    <span className="d-block text-secondary experiment-meta">
                      {(exp.branches ?? []).map((b) => b.branch).join(', ')}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {current?.hypothesis && (
          <p className="text-secondary mt-2 mb-0 prose-column">{current.hypothesis}</p>
        )}
        {current?.replicate_target && (
          <p className="text-secondary mt-2 mb-0">
            Replicate: <code>bazel run {current.replicate_target}</code>
          </p>
        )}
      </div>
      <div className="card-footer d-flex flex-column gap-2">
        <div className="d-flex flex-wrap gap-2">
          {canWrite && (
            <Button variant="primary" size="sm" onClick={() => setDialog('create')}>
              <BsPlusLg className="me-1" /> New
            </Button>
          )}
          {canWrite && current && (
            <>
              <Button variant="outline-secondary" size="sm" onClick={() => setDialog('fork')}>
                Fork
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setDialog('edit')}>
                <BsPencil className="me-1" /> Edit
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={!current.parent_id}
                title={current.parent_id ? '' : 'Already a root'}
                onClick={() => emancipate(current)}
              >
                Emancipate
              </Button>
              <Button
                variant="outline-warning"
                size="sm"
                disabled={!current.parent_id}
                title={current.parent_id ? '' : 'A root has no parent to fold into'}
                onClick={() => consolidate(current)}
              >
                <GoGitMerge className="me-1" /> Consolidate
              </Button>
            </>
          )}
          {isAdmin && current && (
            <Button variant="outline-danger" size="sm" onClick={() => remove(current)}>
              <BsTrash className="me-1" /> Delete
            </Button>
          )}
        </div>
        <div className="form-check form-switch m-0">
          <input
            id="exp-descendants"
            className="form-check-input"
            type="checkbox"
            checked={descendants}
            onChange={(e) => onDescendantsChange(e.target.checked)}
          />
          <label htmlFor="exp-descendants" className="form-check-label text-secondary">
            Include descendant experiments
          </label>
        </div>
        <div className="form-check form-switch m-0">
          <input
            id="exp-closed"
            className="form-check-input"
            type="checkbox"
            checked={showClosed}
            onChange={(e) => onShowClosedChange(e.target.checked)}
          />
          <label htmlFor="exp-closed" className="form-check-label text-secondary">
            Show consolidated and abandoned ({closedCount})
          </label>
        </div>
      </div>
      {dialog && (
        <ExperimentForm
          mode={dialog}
          owner={owner}
          name={name}
          subject={current}
          experiments={experiments}
          onClose={() => setDialog(null)}
          onSaved={(saved) => {
            setDialog(null);
            onChanged(saved?.slug);
          }}
        />
      )}
      {confirmDialog}
    </div>
  );
}
